import IUserSkill from "../../models/userSkills";
import IUser from "../../models/user";
import {
  ConsultantSkillApiActions,
  MyProfilePageActions,
  ViewProfilePageActions,
  AddUserSkillDialogueActions,
  ConsultantProfilePageActions,
} from "../actions";
import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
} from "@ngrx/store";
import IConsultantProfile, {
  IProfileUpdateResult,
} from "@skills-app/models/consultant-profile";
import { ThingSpacePageActions } from "@skills-app/thingspace/actions";
import { HomePageActions } from "@skills-app/home/Actions";
import { AddSkillToProfileActions } from "@skills-app/shared/actions";
import ISkillNodeWithRating from "@skills-app/models/skillNodeWithRating";
import IUserCertification from "@skills-app/models/userCertification";
import ICertification from "@skills-app/models/certification";
import ICreateSkillRatingMapping from "@skills-app/models/createSkillRatingMapping";
import { getSelectedCertifications } from "./consultant-search.reducer";

export interface IUsersLoading {
  readonly currentUser: boolean;
  readonly currentUserProfile: boolean;
  readonly numberOfUsers: boolean;
  readonly numberOfSkills: boolean;
  readonly userHasSkills: boolean;
  readonly updateUserProfile: boolean;
  readonly addUserSkill: boolean;
  readonly selectedUserProfile: boolean;
  readonly currentUserSkills: boolean;
  readonly selectedUserSkills: boolean;
}

const initialLoadingState: IUsersLoading = {
  currentUserProfile: false,
  currentUser: false,
  numberOfUsers: false,
  numberOfSkills: false,
  userHasSkills: false,
  updateUserProfile: false,
  addUserSkill: false,
  selectedUserProfile: false,
  currentUserSkills: false,
  selectedUserSkills: false,
};

export interface IUsersState {
  loading: IUsersLoading;
  currentUser: IUser | null;
  selectedUserSkills: ISkillNodeWithRating[];
  currentUserSkills: ISkillNodeWithRating[];
  currentUserHasSkills: boolean;
  selectedUserHasSkills: boolean;
  filteredCertifications: ICertification[];
  currentUserProfile: IConsultantProfile | null;
  currentUserProfileStatus: IProfileUpdateResult | null;
  numberOfUsers: number;
  numberOfSkills: number;
  selectedUserProfile: IConsultantProfile | null;
  selectedUserProfileStatus: IProfileUpdateResult | null;
  selectedUserProfileId: string | null;
  selectedUserCertifications: IUserCertification[];
  teamMembers: IUserSkill[];
  profileContentLoading: boolean;
  upsertedUserSkills: ICreateSkillRatingMapping[];
  selectedCertification: IUserCertification | null;
}

const initialState: IUsersState = {
  loading: initialLoadingState,
  currentUser: null,
  selectedUserSkills: [],
  currentUserSkills: [],
  currentUserHasSkills: true,
  selectedUserHasSkills: true,
  filteredCertifications: [],
  currentUserProfile: null,
  currentUserProfileStatus: null,
  numberOfUsers: 0,
  numberOfSkills: 0,
  selectedUserProfile: null,
  selectedUserProfileStatus: null,
  selectedUserProfileId: null,
  selectedUserCertifications: [],
  teamMembers: [],
  profileContentLoading: false,
  upsertedUserSkills: [],
  selectedCertification: null,
};

function mapListByUpdatedItems<T extends { Id?: string | null }>(
  current: T[],
  updated: T[],
): T[] {
  const byId = new Map(
    (updated ?? [])
      .filter((x) => !!x?.Id)
      .map((x) => [String(x.Id), x] as const),
  );

  const mergedExisting = (current ?? []).map((item) => {
    const id = item?.Id ? String(item.Id) : "";
    return byId.get(id) ?? item;
  });

  const existingIds = new Set(
    mergedExisting
      .map((x) => x?.Id ?? "")
      .filter((x) => x !== "")
      .map((x) => String(x)),
  );

  const appendedNew = (updated ?? []).filter((item) => {
    const id = item?.Id;
    if (!id) return true;
    return !existingIds.has(String(id));
  });

  return [...mergedExisting, ...appendedNew];
}

export const reducer = createReducer(
  initialState,

  on(MyProfilePageActions.loadPage, (state) => ({
    ...state,
    profileContentLoading: true,
    loading: { ...state.loading, currentUser: true, currentUserProfile: true },
  })),

  on(ConsultantSkillApiActions.populateCurrentUserProfile, (state, action) => ({
    ...state,
    currentUserProfile: action.profile,
    currentUserProfileStatus: {
      LastUpdated: action.profile.LastUpdated,
      IsProfileComplete: action.profile.IsProfileComplete,
      CompletedProfileSections: action.profile.CompletedProfileSections,
      IsProfileDownloadEnabled:
        action.profile.IsProfileDownloadEnabled ?? false,
    },
    profileContentLoading: false,
    loading: { ...state.loading, currentUserProfile: false },
  })),

  on(
    ConsultantSkillApiActions.populateSelectedUserProfile,
    (state, action) => ({
      ...state,
      selectedUserProfile: action.profile,
      selectedUserProfileStatus: {
        LastUpdated: action.profile.LastUpdated,
        IsProfileComplete: action.profile.IsProfileComplete,
        CompletedProfileSections: action.profile.CompletedProfileSections,
        IsProfileDownloadEnabled:
          action.profile.IsProfileDownloadEnabled ?? false,
      },
      profileContentLoading: false,
      loading: { ...state.loading, selectedUserProfile: false },
    }),
  ),

  on(ViewProfilePageActions.loadPage, (state, action) => ({
    ...state,
    selectedUserProfileId: action.azureId,
    selectedUserProfile: null,
    selectedUserProfileStatus: null,
    selectedUserSkills: [],
    selectedUserCertifications: [],
    loading: { ...state.loading, selectedUserProfile: true },
  })),

  on(ViewProfilePageActions.loadPageError, (state) => ({
    ...state,
    selectedUserProfile: null,
    selectedUserProfileStatus: null,
    loading: { ...state.loading, selectedUserProfile: false },
  })),

  on(ViewProfilePageActions.unloadPage, (state) => ({
    ...state,
    selectedUserProfileId: null,
    selectedUserProfile: null,
    selectedUserProfileStatus: null,
    selectedUserSkills: [],
    selectedUserCertifications: [],
    loading: { ...state.loading, selectedUserProfile: false },
  })),

  on(ConsultantSkillApiActions.requestAllUsersCount, (state) => ({
    ...state,
    loading: { ...state.loading, numberOfUsers: true },
  })),

  on(
    ConsultantSkillApiActions.requestAllUsersCountSuccess,
    (state, action) => ({
      ...state,
      numberOfUsers: action.count,
      loading: { ...state.loading, numberOfUsers: false },
    }),
  ),

  on(ConsultantSkillApiActions.requestAllUsersCountError, (state) => ({
    ...state,
    loading: { ...state.loading, numberOfUsers: false },
  })),

  on(
    ConsultantSkillApiActions.requestUserHasSkillsSuccess,
    (state, action) => ({
      ...state,
      currentUserHasSkills: action.hasSkills,
      loading: { ...state.loading, userHasSkills: false },
    }),
  ),

  on(ConsultantSkillApiActions.requestUserHasSkillsError, (state) => ({
    ...state,
    loading: { ...state.loading, userHasSkills: false },
  })),

  on(ConsultantSkillApiActions.requestAllSkillCount, (state) => ({
    ...state,
    loading: { ...state.loading, numberOfSkills: true },
  })),

  on(
    ConsultantSkillApiActions.requestAllSkillCountSuccess,
    (state, action) => ({
      ...state,
      numberOfSkills: action.count,
      loading: { ...state.loading, numberOfSkills: false },
    }),
  ),

  on(ConsultantSkillApiActions.requestAllSkillCountError, (state) => ({
    ...state,
    loading: { ...state.loading, numberOfSkills: false },
  })),

  on(ConsultantSkillApiActions.requestCurrentUserSkills, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      currentUserSkills: true,
    },
  })),

  on(
    ConsultantSkillApiActions.requestCurrentUserSkillsSuccess,
    (state, action) => ({
      ...state,
      currentUserSkills: action.skills,
      loading: {
        ...state.loading,
        currentUserSkills: false,
      },
    }),
  ),

  on(ConsultantSkillApiActions.requestCurrentUserSkillsError, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      currentUserSkills: false,
    },
  })),

  on(ConsultantSkillApiActions.requestSelectedUserSkills, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      selectedUserSkills: true,
    },
  })),

  on(
    ConsultantSkillApiActions.requestSelectedUserSkillsSuccess,
    ConsultantProfilePageActions.populateUserSkills,
    (state, action) => ({
      ...state,
      selectedUserSkills: action.skills,
      loading: {
        ...state.loading,
        selectedUserSkills: false,
      },
    }),
  ),

  on(ConsultantProfilePageActions.updateUserSkills, (state, action) => ({
    ...state,
    selectedUserSkills: mapListByUpdatedItems(
      state.selectedUserSkills,
      action.skills,
    ),
    loading: {
      ...state.loading,
      selectedUserSkills: false,
      currentUserSkills: false,
    },
  })),

  on(ConsultantSkillApiActions.requestSelectedUserSkillsError, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      selectedUserSkills: false,
    },
  })),

  on(
    ViewProfilePageActions.selectAddSkillUser,
    AddUserSkillDialogueActions.selectAddSkillUser,
    (state, action) => ({
      ...state,
      selectedUserProfileId: action.azureId,
    }),
  ),

  on(
    MyProfilePageActions.requestUpdateUserSkill,
    AddUserSkillDialogueActions.requestUpdateUserSkill,
    AddSkillToProfileActions.requestUpdateUserSkill,
    (state, action) => ({
      ...state,
      currentUserSkills:
        action.azureId &&
        state.currentUser?.AzureId &&
        action.azureId === state.currentUser.AzureId
          ? [
              ...state.currentUserSkills.filter(
                (skill) => skill.Id !== action.skillMapping.Id,
              ),
              action.skillMapping,
            ]
          : state.currentUserSkills,
      selectedUserSkills:
        action.azureId &&
        state.selectedUserProfileId &&
        action.azureId === state.selectedUserProfileId
          ? [
              ...state.selectedUserSkills.filter(
                (skill) => skill.Id !== action.skillMapping.Id,
              ),
              action.skillMapping,
            ]
          : state.selectedUserSkills,
      loading: { ...state.loading, addUserSkill: true },
    }),
  ),

  on(MyProfilePageActions.temporaryUpdateUserSkill, (state, action) => ({
    ...state,
    currentUserSkills:
      action.azureId &&
      state.currentUser?.AzureId &&
      action.azureId === state.currentUser.AzureId
        ? [
            ...state.currentUserSkills.filter(
              (skill) => skill.Id !== action.skillMapping.Id,
            ),
            { ...action.skillMapping, IsTemporary: true } as any,
          ]
        : state.currentUserSkills,
    selectedUserSkills:
      action.azureId &&
      state.selectedUserProfileId &&
      action.azureId === state.selectedUserProfileId
        ? [
            ...state.selectedUserSkills.filter(
              (skill) => skill.Id !== action.skillMapping.Id,
            ),
            { ...action.skillMapping, IsTemporary: true } as any,
          ]
        : state.selectedUserSkills,
    loading: { ...state.loading, addUserSkill: true },
  })),

  on(MyProfilePageActions.reorderSkills, (state, action) => {
    const orderMap = new Map((action.skills ?? []).map((s) => [s.Id, s.Order]));
    const applyOrder = (list: any[]) =>
      (list ?? []).map((s) =>
        orderMap.has(s.Id) ? { ...s, Order: orderMap.get(s.Id) } : s,
      );

    return {
      ...state,
      currentUserSkills:
        action.azureId &&
        state.currentUser?.AzureId &&
        action.azureId === state.currentUser.AzureId
          ? applyOrder(state.currentUserSkills)
          : state.currentUserSkills,
      selectedUserSkills:
        action.azureId &&
        state.selectedUserProfileId &&
        action.azureId === state.selectedUserProfileId
          ? applyOrder(state.selectedUserSkills)
          : state.selectedUserSkills,
    };
  }),

  on(MyProfilePageActions.removeTemporaryUserSkill, (state, action) => ({
    ...state,
    currentUserSkills: state.currentUserSkills.filter(
      (skill) => skill.Id !== action.skillMapping.Id,
    ),
    selectedUserSkills: state.selectedUserSkills.filter(
      (skill) => skill.Id !== action.skillMapping.Id,
    ),
    loading: { ...state.loading, addUserSkill: true },
  })),

  on(
    MyProfilePageActions.requestDeleteUserSkill,
    AddSkillToProfileActions.requestDeleteUserSkill,
    (state, action) => ({
      ...state,
      currentUserSkills:
        action.deleteUserSkill?.AzureId &&
        state.currentUser?.AzureId &&
        action.deleteUserSkill.AzureId === state.currentUser.AzureId
          ? state.currentUserSkills.filter(
              (skill) => skill.Id !== action.deleteUserSkill.SkillId,
            )
          : state.currentUserSkills,
      selectedUserSkills:
        action.deleteUserSkill?.AzureId &&
        state.selectedUserProfileId &&
        action.deleteUserSkill.AzureId === state.selectedUserProfileId
          ? state.selectedUserSkills.filter(
              (skill) => skill.Id !== action.deleteUserSkill.SkillId,
            )
          : state.selectedUserSkills,
      loading: { ...state.loading, addUserSkill: true },
    }),
  ),

  on(
    MyProfilePageActions.requestDeleteMultipleUserSkills,
    AddSkillToProfileActions.requestDeleteMultipleUserSkills,
    (state, action) => ({
      ...state,
      currentUserSkills:
        action.deleteUserSkills?.AzureId &&
        state.currentUser?.AzureId &&
        action.deleteUserSkills.AzureId === state.currentUser.AzureId
          ? state.currentUserSkills.filter(
              (skill) =>
                !action.deleteUserSkills.SkillIds.some((x) => x === skill.Id),
            )
          : state.currentUserSkills,
      selectedUserSkills:
        action.deleteUserSkills?.AzureId &&
        state.selectedUserProfileId &&
        action.deleteUserSkills.AzureId === state.selectedUserProfileId
          ? state.selectedUserSkills.filter(
              (skill) =>
                !action.deleteUserSkills.SkillIds.some((x) => x === skill.Id),
            )
          : state.selectedUserSkills,
      loading: { ...state.loading, addUserSkill: true },
    }),
  ),

  on(
    ConsultantSkillApiActions.updateUserSkillSuccess,
    ConsultantSkillApiActions.deleteUserSkillSuccess,
    (state) => ({
      ...state,
      loading: { ...state.loading, addUserSkill: false },
    }),
  ),

  on(MyProfilePageActions.requestCurrentUserProfile, (state) => ({
    ...state,
    loading: { ...state.loading, currentUserProfile: true },
  })),

  on(MyProfilePageActions.getCurrentUserProfileSuccess, (state) => ({
    ...state,
    loading: { ...state.loading, currentUserProfile: false },
  })),

  on(MyProfilePageActions.requestUpdateUserProfile, (state, action) => ({
    ...state,
    currentUserProfile: state.currentUserProfile
      ? {
          ...state.currentUserProfile,
          ...action.profile,
        }
      : action.profile,
    loading: { ...state.loading, updateUserProfile: true },
  })),

  on(MyProfilePageActions.updateUserProfileSuccess, (state, action) => ({
    ...state,
    currentUserProfileStatus: action.profileUpdateResult,
    currentUserProfile: state.currentUserProfile
      ? {
          ...state.currentUserProfile,
          LastUpdated: action.profileUpdateResult.LastUpdated,
          IsProfileComplete: action.profileUpdateResult.IsProfileComplete,
          CompletedProfileSections:
            action.profileUpdateResult.CompletedProfileSections,
          IsProfileDownloadEnabled:
            action.profileUpdateResult.IsProfileDownloadEnabled ?? false,
        }
      : state.currentUserProfile,
    loading: { ...state.loading, updateUserProfile: false },
  })),

  on(
    MyProfilePageActions.getCurrentUserProfileStatusSuccess,
    (state, action) => ({
      ...state,
      currentUserProfileStatus: {
        LastUpdated: action.profileStatus?.LastUpdated,
        IsProfileComplete: action.profileStatus?.IsProfileComplete,
        CompletedProfileSections:
          action.profileStatus?.CompletedProfileSections,
        IsProfileDownloadEnabled:
          action.profileStatus?.IsProfileDownloadEnabled ?? false,
      },
    }),
  ),

  on(MyProfilePageActions.updateUserProfileError, (state) => ({
    ...state,
    loading: { ...state.loading, updateUserProfile: false },
  })),

  on(ThingSpacePageActions.loadPage, HomePageActions.loadPage, (state) => ({
    ...state,
    loading: { ...state.loading, currentUser: true },
  })),

  on(ConsultantSkillApiActions.populateUser, (state, action) => ({
    ...state,
    selectedUserProfileId: action.user.AzureId,
    currentUser: action.user,
    loading: { ...state.loading, currentUser: false },
  })),

  on(MyProfilePageActions.requestUpsertUserSkill, (state, action) => {
    const mappedUpsertSkill: ICreateSkillRatingMapping & {
      Id?: string;
      Name?: string;
      IsTemporary?: boolean;
    } = {
      SkillId: action.upsertedSkill.Id,
      SkillRating: action.upsertedSkill.SkillRating,
      SkillIsHiddenFromProfile: action.upsertedSkill.IsHiddenFromProfile,
      Id: action.upsertedSkill.SkillType?.Id ?? "",
      Name: action.upsertedSkill.Name,
      IsTemporary: false,
    } as any;

    return {
      ...state,
      upsertedUserSkills: [
        ...state.upsertedUserSkills.filter(
          (s) => s.SkillId !== action.upsertedSkill.Id,
        ),
        mappedUpsertSkill,
      ],
      currentUserSkills: [
        ...state.currentUserSkills.filter(
          (skill) => skill.Id !== action.upsertedSkill.Id,
        ),
        { ...action.upsertedSkill, IsTemporary: false },
      ],
      selectedUserSkills: [
        ...state.selectedUserSkills.filter(
          (skill) => skill.Id !== action.upsertedSkill.Id,
        ),
        { ...action.upsertedSkill, IsTemporary: false },
      ],
    };
  }),

  on(MyProfilePageActions.clearUpsertedSkills, (state) => ({
    ...state,
    upsertedUserSkills: [],
  })),

  on(MyProfilePageActions.requestSaveUpsertedSkillsError, (state, action) => ({
    ...state,
    upsertedUserSkills: [
      ...action.unsavedSkills.filter(
        (s) =>
          !state.upsertedUserSkills.map((u) => u.SkillId).includes(s.SkillId),
      ),
      ...state.upsertedUserSkills,
    ],
  })),

  on(
    MyProfilePageActions.requestDeleteUserCertificationSuccess,
    (state, action) => {
      const nextCerts =
        state.selectedUserCertifications &&
        state.selectedUserCertifications.length >= 1
          ? state.selectedUserCertifications.filter(
              (cert) => cert.Id != action.userCertificationId,
            )
          : state.selectedUserCertifications;

      const hasCertifications = (nextCerts?.length ?? 0) > 0;

      const updateSections = (current: any) => {
        if (!current) return current;
        const currentSections = current.CompletedProfileSections ?? 0;
        const certificationsBit = 1 << 3;
        const nextSections = hasCertifications
          ? currentSections | certificationsBit
          : currentSections & ~certificationsBit;
        return {
          ...current,
          CompletedProfileSections: nextSections,
        };
      };

      return {
        ...state,
        selectedUserCertifications: nextCerts,
        currentUserProfileStatus: state.currentUserProfileStatus
          ? {
              ...updateSections(state.currentUserProfileStatus),
            }
          : state.currentUserProfileStatus,
        currentUserProfile: state.currentUserProfile
          ? {
              ...updateSections(state.currentUserProfile),
            }
          : state.currentUserProfile,
      };
    },
  ),

  on(
    MyProfilePageActions.requestFilterCertificationsSuccess,
    (state, action) => ({
      ...state,
      filteredCertifications: action.certifications,
    }),
  ),

  on(MyProfilePageActions.clearFilteredCertifications, (state) => ({
    ...state,
    filteredCertifications: [],
  })),

  on(
    MyProfilePageActions.requestCurrentUserCertificationsSuccess,
    (state, action) => ({
      ...state,
      selectedUserCertifications: action.userCertifications,
    }),
  ),

  on(
    MyProfilePageActions.requestUpsertUserCertificationsSuccess,
    (state, action) => {
      const hasCertifications = (action.certifications?.length ?? 0) > 0;

      const updateSections = (current: any) => {
        if (!current) return current;
        const currentSections = current.CompletedProfileSections ?? 0;
        const certificationsBit = 1 << 3;
        const nextSections = hasCertifications
          ? currentSections | certificationsBit
          : currentSections & ~certificationsBit;
        return {
          ...current,
          CompletedProfileSections: nextSections,
        };
      };

      return {
        ...state,
        selectedUserCertifications: action.certifications,
        currentUserProfileStatus: state.currentUserProfileStatus
          ? {
              ...updateSections(state.currentUserProfileStatus),
            }
          : state.currentUserProfileStatus,
        currentUserProfile: state.currentUserProfile
          ? {
              ...updateSections(state.currentUserProfile),
            }
          : state.currentUserProfile,
      };
    },
  ),

  on(
    MyProfilePageActions.requestSelectedUserCertificationsSuccess,
    (state, action) => ({
      ...state,
      selectedUserCertifications: action.userCertifications,
    }),
  ),
);

export const getConsultantsState = createFeatureSelector<IUsersState>("users");

export const getConsultantLoading = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.loading,
);

export const getCurrentUserProfile = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.currentUserProfile,
);

export const getCurrentUserProfileStatus = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.currentUserProfileStatus,
);

export const getSelectedUserProfileStatus = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.selectedUserProfileStatus,
);

export const getSelectedUserSkills = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.selectedUserSkills,
);

export const getUpsertedUserSkills = createSelector(
  getConsultantsState,
  (state: any) => state?.upsertedUserSkills ?? [],
);

export const getSelectedUserCertifications = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.selectedUserCertifications,
);

export const getSelectedSortedUserCertifications = createSelector(
  getConsultantsState,
  (consultantsState) =>
    consultantsState.selectedUserCertifications?.slice().sort((a, b) => {
      return (
        Number(a.IsExpired) - Number(b.IsExpired) ||
        a.Certification?.Title.localeCompare(b.Certification?.Title)
      );
    }),
);

export const getCurrentUserHasSkills = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.currentUserHasSkills,
);

export const getSelectedUserHasSkills = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.selectedUserHasSkills,
);

export const getAllUsersCount = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.numberOfUsers,
);

export const getAllSkillsCount = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.numberOfSkills,
);

export const getCurrentConsultant = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.currentUser,
);

export const getSelectedConsultant = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.selectedUserProfile as IUser,
);

export const getProfileContentLoading = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.profileContentLoading,
);

export const getSelectedProfileLoading = createSelector(
  getConsultantsState,
  (consultantState) => consultantState.loading.selectedUserProfile,
);

export const getCurrentUserLoading = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.loading.currentUser,
);

export const getSelectedUserProfile = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.selectedUserProfile,
);

export const getSelectedUserCurrentCertifications = createSelector(
  getSelectedSortedUserCertifications,
  (certifications) => (certifications ?? []).filter((x) => !x.IsExpired),
);

export const getSelectedUserExpiredCertifications = createSelector(
  getSelectedSortedUserCertifications,
  (certifications) => (certifications ?? []).filter((x) => x.IsExpired),
);

export const getFilteredCertifications = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.filteredCertifications,
);

export const getFilteredCertificationsExcludingSelectedUsers = createSelector(
  getFilteredCertifications,
  getSelectedUserCertifications,
  (filteredCertifications, userCertifications) => {
    if (
      filteredCertifications &&
      filteredCertifications.length >= 1 &&
      userCertifications &&
      userCertifications.length >= 1
    ) {
      return filteredCertifications.filter(
        (cert) =>
          !userCertifications.map((c) => c.Certification.Id).includes(cert.Id),
      );
    }
    return filteredCertifications;
  },
);

export const getFilteredCertificationsExcludingSelected = createSelector(
  getFilteredCertifications,
  getSelectedCertifications,
  (filteredCertifications, selectedCertifications) => {
    if (
      filteredCertifications &&
      filteredCertifications.length >= 1 &&
      selectedCertifications &&
      selectedCertifications.length >= 1
    ) {
      return filteredCertifications.filter(
        (cert) => !selectedCertifications.map((c) => c.Id).includes(cert.Id),
      );
    }
    return filteredCertifications;
  },
);

export const getCurrentUserSkills = createSelector(
  getConsultantsState,
  (consultantsState) => consultantsState.currentUserSkills,
);

export const getCurrentConsultantsSkillsShownInProfile = createSelector(
  getCurrentUserSkills,
  (currentUserSkills) =>
    currentUserSkills?.filter((s) => !s.IsHiddenFromProfile && s.IsActive) ||
    [],
);

export const getCurrentConsultantsSkillsHiddenFromProfile = createSelector(
  getCurrentUserSkills,
  (currentUserSkills) =>
    currentUserSkills?.filter((s) => s.IsHiddenFromProfile && s.IsActive) || [],
);

export const getCurrentConsultantsSkillsDeletedFromSkillsApp = createSelector(
  getCurrentUserSkills,
  (currentUserSkills) => currentUserSkills?.filter((s) => !s.IsActive) || [],
);

export const getSelectedConsultantsSkillsShownInProfile = createSelector(
  getSelectedUserSkills,
  (selectedUserSkills) =>
    selectedUserSkills?.filter((s) => !s.IsHiddenFromProfile && s.IsActive) ||
    [],
);

export const getSelectedConsultantsSkillsHiddenFromProfile = createSelector(
  getSelectedUserSkills,
  (selectedUserSkills) =>
    selectedUserSkills?.filter((s) => s.IsHiddenFromProfile && s.IsActive) ||
    [],
);

export const getSelectedConsultantsSkillsDeletedFromSkillsApp = createSelector(
  getSelectedUserSkills,
  (selectedUserSkills) => selectedUserSkills?.filter((s) => !s.IsActive) || [],
);

export const getSelectedSkillsShownInProfile = createSelector(
  getSelectedUserSkills,
  (selectedUserSkills) =>
    selectedUserSkills && selectedUserSkills.length > 0
      ? selectedUserSkills.filter((s) => !s.IsHiddenFromProfile && s.IsActive)
      : selectedUserSkills,
);

/**
 * Parity: downloads enabled by default when profile exists.
 * Only disable when API explicitly returns IsProfileDownloadEnabled === false.
 */
function isDownloadEnabledFromFlag(flag: boolean | null | undefined): boolean {
  return flag !== false;
}

export const getCurrentUserProfileDownloadEnabled = createSelector(
  getCurrentUserProfile,
  (profile) =>
    !!profile && isDownloadEnabledFromFlag(profile.IsProfileDownloadEnabled),
);

export const getCurrentUserProfileStatusDownloadEnabled = createSelector(
  getCurrentUserProfileStatus,
  (status) =>
    !!status && isDownloadEnabledFromFlag(status.IsProfileDownloadEnabled),
);

export const getSelectedUserProfileDownloadEnabled = createSelector(
  getSelectedUserProfile,
  (profile) =>
    !!profile && isDownloadEnabledFromFlag(profile.IsProfileDownloadEnabled),
);

export const getSelectedUserProfileStatusDownloadEnabled = createSelector(
  getSelectedUserProfileStatus,
  (status) =>
    !!status && isDownloadEnabledFromFlag(status.IsProfileDownloadEnabled),
);
